import { useCallback, useEffect, useRef, useState } from 'react'

/** Toast kecil untuk konfirmasi copy path (tanpa dependency Mantine) */
function showToast(text: string) {
  const el = document.createElement('div')
  Object.assign(el.style, {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontFamily: 'monospace',
    zIndex: '100001',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'opacity 0.3s ease',
  })
  el.textContent = `Copied: ${text}`
  document.body.appendChild(el)
  setTimeout(() => {
    el.style.opacity = '0'
    setTimeout(() => el.remove(), 300)
  }, 2000)
}

/**
 * Dev Inspector — cari source code info dari elemen yang diklik.
 * Strategi: fiber props → DOM attributes → walk up.
 * Safe untuk React 19.
 */

interface CodeInfo {
  relativePath: string
  line: string
  column: string
}

/** Baca data-inspector-* dari fiber pendingProps atau __reactProps$ */
function getCodeInfoFromElement(element: HTMLElement): CodeInfo | null {
  // 1. Coba baca dari React internal props (paling akurat)
  for (const key of Object.keys(element)) {
    if (key.startsWith('__reactProps$')) {
      const props = (element as any)[key]
      if (props?.['data-inspector-relative-path']) {
        return {
          relativePath: props['data-inspector-relative-path'],
          line: props['data-inspector-line'] || '1',
          column: props['data-inspector-column'] || '1',
        }
      }
    }
    if (key.startsWith('__reactFiber$')) {
      const fiber = (element as any)[key]
      // Walk up fiber tree
      let f = fiber
      while (f) {
        const p = f.pendingProps || f.memoizedProps
        if (p?.['data-inspector-relative-path']) {
          return {
            relativePath: p['data-inspector-relative-path'],
            line: p['data-inspector-line'] || '1',
            column: p['data-inspector-column'] || '1',
          }
        }
        // Cek _debugSource juga (React < 19)
        const src = f._debugSource ?? f._debugOwner?._debugSource
        if (src?.fileName && src?.lineNumber) {
          return {
            relativePath: src.fileName,
            line: String(src.lineNumber),
            column: String(src.columnNumber ?? 1),
          }
        }
        f = f.return
      }
    }
  }

  // 2. Fallback: baca DOM attribute langsung
  const rp = element.getAttribute('data-inspector-relative-path')
  if (rp) {
    return {
      relativePath: rp,
      line: element.getAttribute('data-inspector-line') || '1',
      column: element.getAttribute('data-inspector-column') || '1',
    }
  }

  return null
}

/** Walk up DOM tree dan coba setiap elemen */
function findCodeInfo(target: HTMLElement): CodeInfo | null {
  let el: HTMLElement | null = target
  while (el) {
    const info = getCodeInfoFromElement(el)
    if (info) return info
    el = el.parentElement
  }
  return null
}

function openInEditor(info: CodeInfo) {
  fetch('/__open-in-editor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      relativePath: info.relativePath,
      lineNumber: info.line,
      columnNumber: info.column,
    }),
  })
}

interface DevInspectorProps {
  children: React.ReactNode
  /** true = copy path only (production), false = open editor + copy (dev) */
  copyOnly?: boolean
  /** Jika true, inspector dinonaktifkan total (role tidak punya akses) */
  disabled?: boolean
}

export function DevInspector({ children, copyOnly = false, disabled = false }: DevInspectorProps) {
  if (disabled) return <>{children}</>

  return <DevInspectorInner copyOnly={copyOnly}>{children}</DevInspectorInner>
}

function DevInspectorInner({ children, copyOnly }: { children: React.ReactNode; copyOnly: boolean }) {
  const [active, setActive] = useState(false)
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const lastInfoRef = useRef<CodeInfo | null>(null)

  const updateOverlay = useCallback((target: HTMLElement | null) => {
    const ov = overlayRef.current
    const tt = tooltipRef.current
    if (!ov || !tt) return

    if (!target) {
      ov.style.display = 'none'
      tt.style.display = 'none'
      lastInfoRef.current = null
      return
    }

    const info = findCodeInfo(target)
    if (!info) {
      ov.style.display = 'none'
      tt.style.display = 'none'
      lastInfoRef.current = null
      return
    }

    lastInfoRef.current = info

    // Highlight elemen yang dihover
    const rect = target.getBoundingClientRect()
    ov.style.display = 'block'
    ov.style.top = `${rect.top + window.scrollY}px`
    ov.style.left = `${rect.left + window.scrollX}px`
    ov.style.width = `${rect.width}px`
    ov.style.height = `${rect.height}px`

    tt.style.display = 'block'
    tt.textContent = `${info.relativePath}:${info.line}`
    const ttTop = rect.top + window.scrollY - 24
    tt.style.top = `${ttTop > 0 ? ttTop : rect.bottom + window.scrollY + 4}px`
    tt.style.left = `${rect.left + window.scrollX}px`
  }, [])

  useEffect(() => {
    if (!active) return

    const onMouseOver = (e: MouseEvent) => updateOverlay(e.target as HTMLElement)

    const onClick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const info = lastInfoRef.current ?? findCodeInfo(e.target as HTMLElement)
      if (info) {
        const loc = `${info.relativePath}:${info.line}:${info.column}`
        navigator.clipboard.writeText(loc)
        if (copyOnly) {
          showToast(loc)
        } else {
          openInEditor(info)
        }
      }
      setActive(false)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(false)
    }

    document.addEventListener('mouseover', onMouseOver, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKeyDown)
    document.body.style.cursor = 'crosshair'

    return () => {
      document.removeEventListener('mouseover', onMouseOver, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.cursor = ''
      if (overlayRef.current) overlayRef.current.style.display = 'none'
      if (tooltipRef.current) tooltipRef.current.style.display = 'none'
    }
  }, [active, updateOverlay, copyOnly])

  // Hotkey: Ctrl+Shift+Cmd+C (macOS) / Ctrl+Shift+Alt+C
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'c' && e.ctrlKey && e.shiftKey && (e.metaKey || e.altKey)) {
        e.preventDefault()
        setActive((prev) => {
          console.log(`[DevInspector] ${prev ? 'Deactivated' : 'Activated'}`)
          return !prev
        })
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <>
      {children}
      <div
        ref={overlayRef}
        style={{
          display: 'none',
          position: 'absolute',
          pointerEvents: 'none',
          border: '2px solid #3b82f6',
          backgroundColor: 'rgba(59,130,246,0.1)',
          zIndex: 99999,
          transition: 'all 0.05s ease',
        }}
      />
      <div
        ref={tooltipRef}
        style={{
          display: 'none',
          position: 'absolute',
          pointerEvents: 'none',
          backgroundColor: '#1e293b',
          color: '#e2e8f0',
          fontSize: '12px',
          fontFamily: 'monospace',
          padding: '2px 6px',
          borderRadius: '3px',
          zIndex: 100000,
          whiteSpace: 'nowrap',
        }}
      />
    </>
  )
}
